import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, EventEmitter, Output } from '@angular/core';
import {
  AbstractControlOptions,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import {
  DateAdapter,
  MAT_DATE_LOCALE,
  MatNativeDateModule,
  NativeDateAdapter,
} from '@angular/material/core';
import {
  MatDatepicker,
  MatDatepickerModule,
} from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormFieldEvent } from '@models/forms.interface';

export class CustomDateAdapter extends NativeDateAdapter {
  override format(date: Date): string {
    const month = date.getMonth() + 1;
    const yyyy = date.getFullYear();
    const mm = month.toString().length === 1 ? '0' + month : month;

    return `${mm}/${yyyy}`;
  }
}

@Component({
  selector: 'app-date-field',
  standalone: true,
  imports: [
    MatInputModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatNativeDateModule,
    CommonModule,
    ReactiveFormsModule,
  ],
  providers: [
    {
      provide: DateAdapter,
      useClass: CustomDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
  ],
  templateUrl: './date-field.component.html',
  styleUrl: './date-field.component.scss',
})
export class DateFieldComponent implements OnInit {
  @Input() name = '';
  @Input() label = '';
  @Input() value = '';
  @Input() isRequired = false;
  @Input() customErrorMessage = '';
  @Output() dateFieldEvent = new EventEmitter<FormFieldEvent>();

  public dateField!: FormGroup;
  public currentYear: number = new Date().getFullYear();
  public maxOneYearFromNow: Date = new Date(
    new Date().setFullYear(this.currentYear + 1)
  );
  public earliestQueriedReportDate: Date = new Date('June 01, 1991 00:00:00');

  ngOnInit(): void {
    this.buildForm();
  }

  public buildForm(): void {
    const validators: ValidatorFn[] = [];

    if (this.isRequired) {
      validators.push(Validators.required);
    }

    this.dateField = new FormGroup(
      {
        date: new FormControl<Date | string>('', {
          validators,
          nonNullable: true,
        }),
      },
      {
        updateOn: 'change',
      } as AbstractControlOptions
    );

    this.dateFieldEvent.emit({
      value: '',
      name: this.name,
    });
  }

  public setMonthAndYear(
    fullDate: Date,
    datePicker: MatDatepicker<unknown>
  ): void {
    this.setDate('date', fullDate);
    this.emitDateChange();
    datePicker.close();
  }

  public handleDateChange(input: string): void {
    // const input = event?.value;

    if (!input?.length) {
      this.dateField.get('date')?.setErrors({ required: true });
      this.emitDateChange();
      return;
    }

    const regex = /^(0[1-9]|1[0-2])\/(\d{4})$/g;
    const isValidDate = input.match(regex);

    if (!isValidDate) {
      this.dateField.get('date')?.setErrors({ invalidDate: true });
      this.emitDateChange();
      return;
    }

    const [mm, yyyy] = input.split('/');
    this.setDate('date', new Date(+yyyy, +mm - 1));
    this.dateField.get('date')?.setErrors(null);
    this.emitDateChange();
  }

  private emitDateChange(): void {
    const errors = this.dateField.get('date')?.errors
      ? this.dateField.get('date')?.errors
      : this.dateField?.errors;
  
    this.dateFieldEvent.emit({
      name: this.name,
      value: this.getDate('date'),
      errors,
    });
  }

  private getDate(controlName: string) {
    return this.dateField.get(controlName)?.value;
  }

  private setDate(controlName: string, value: Date): void {
    this.dateField.get(controlName)?.setValue(value);
  }
}
